/**
 * Temu 属性联动：展示条件（showCondition）+ 本地兜底（同名编号组 / 无里料）。
 * Editor 形态属性（attributeId / values / temu_prop）与 attrs.get 原生形态均可。
 */

import { attributeDisplayLabel, attributeValueDisplayLabel } from '@/temu-platform/utils/temuAttributeTemplate';

function keyOf(prop) {
  return String(prop?.attributeId || prop?.templatePid || prop?.pid || '');
}

function temuMeta(attr) {
  return attr?.temu_prop && typeof attr.temu_prop === 'object' ? attr.temu_prop : null;
}

export function attrShowCondition(attr) {
  const meta = temuMeta(attr);
  const list = attr?.showCondition ?? meta?.showCondition;
  return Array.isArray(list) ? list : [];
}

export function attrParentTemplatePid(attr) {
  const meta = temuMeta(attr);
  const v = attr?.parentTemplatePid ?? meta?.parentTemplatePid;
  // 平台常下发 0 / null 表示无父属性；勿把 0 当成有效父模板 id（否则根属性会被全部隐藏）
  const n = v != null && String(v).trim() !== '' ? Number(v) : null;
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function attrRefPid(attr) {
  const meta = temuMeta(attr);
  const v = attr?.refPid ?? meta?.refPid ?? attr?.pid ?? meta?.pid;
  return v != null && Number(v) > 0 ? Number(v) : null;
}

/** 从表单值解析已选 vid / 文本 */
export function resolveEditorAttrSelection(productAttributes, attr) {
  const id = keyOf(attr);
  if (!id) return null;
  const raw = productAttributes?.[id] ?? productAttributes?.[attr?.attributeId];
  if (raw == null || raw === '') return null;
  if (Array.isArray(raw)) {
    const first = raw.find((row) => {
      if (row && typeof row === 'object') {
        return Boolean(row.valueId ?? row.attributeValueId ?? row.percent ?? row.attributeExtraValue);
      }
      return row != null && String(row).trim() !== '';
    });
    if (!first) return null;
    if (first && typeof first === 'object') {
      const vid = first.valueId ?? first.attributeValueId ?? first.vid;
      const label = first.name || first.label || first.attributeExtraValue || '';
      return {
        vid: vid != null && String(vid).trim() !== '' ? String(vid) : null,
        propValue: String(label || '').trim(),
        raw: first,
      };
    }
    return { vid: String(first), propValue: String(first), raw: first };
  }
  if (typeof raw === 'object') {
    const vid = raw.valueId ?? raw.attributeValueId ?? raw.vid;
    const label = raw.name || raw.label || raw.attributeExtraValue || '';
    return {
      vid: vid != null && String(vid).trim() !== '' ? String(vid) : null,
      propValue: String(label || '').trim(),
      raw,
    };
  }
  const text = String(raw).trim();
  if (!text) return null;
  // 手输数字/文本：无 vid；兼容表单里存了展示名而非 id 的情况
  const asOpt = (attr?.values || []).find((v) => String(v.id) === text)
    || (attr?.values || []).find((v) => {
      const label = attributeValueDisplayLabel(v);
      return label && label === text;
    })
    || (attr?.values || []).find((v) => {
      const name = String(v?.name || v?.nameCn || v?.label || '').trim();
      return name && name === text;
    });
  // 有枚举选项的属性不算手输；勿因 controlType=0 丢掉 vid（否则父属性匹配失败、下拉选不中）
  const mode = Number(attr?.mode);
  const handInput = (mode === 0 && !(attr?.values || []).length)
    || (!(attr?.values || []).length && !asOpt);
  return {
    vid: asOpt ? String(asOpt.id) : (handInput ? null : (/^\d+$/.test(text) ? text : null)),
    propValue: asOpt ? attributeValueDisplayLabel(asOpt) : text,
    raw,
  };
}

export function isEditorAttrFilled(attr, productAttributes) {
  const id = keyOf(attr);
  const raw = productAttributes?.[id] ?? productAttributes?.[attr?.attributeId];
  if (raw == null || raw === '') return false;
  if (attr?.mode === 4 || attr?.isComposition) {
    const rows = Array.isArray(raw) ? raw : [];
    return rows.some((row) => {
      if (!row || typeof row !== 'object') return false;
      return Boolean(row.valueId || row.attributeValueId) && Number(row.percent) > 0;
    });
  }
  if (attr?.mode === 1) {
    return Array.isArray(raw) && raw.length > 0;
  }
  const sel = resolveEditorAttrSelection(productAttributes, attr);
  return Boolean(sel && (sel.vid || sel.propValue));
}

function selectedValueLabel(productAttributes, attr) {
  const sel = resolveEditorAttrSelection(productAttributes, attr);
  if (!sel) return '';
  if (sel.propValue) return sel.propValue;
  if (!sel.vid) return '';
  const hit = (attr?.values || []).find((v) => String(v.id) === String(sel.vid));
  return attributeValueDisplayLabel(hit) || String(sel.vid);
}

export function isNoneLikeAttrValue(label) {
  const t = String(label || '').trim();
  if (!t) return false;
  // 含「无里」（无里 / 无里料 / 无里衬）以及无内衬等
  return /无里|无内衬|无中里衬|无衬里|无衬|尤为衬|不配里|without\s*lining|no\s*lining|\bnone\b|\bn\/?a\b|^无$/i.test(t);
}

/** 里衬/里料父属性：类型/纹理，或字段本身就叫「里料/里衬」 */
export function isLiningParentAttr(attr) {
  const nameOnly = String(attr?.name || '').trim();
  if (/^(里料|里衬|中里衬|lining)$/i.test(nameOnly)) return true;
  const n = `${attr?.name || ''} ${attr?.nameEn || ''}`;
  if (!/里衬|里料|lining|里布|中里衬/i.test(n)) return false;
  return /类型|纹理|texture|type|材质/i.test(n);
}

/** @deprecated 兼容旧名，等同 isLiningParentAttr */
export function isLiningTextureAttr(attr) {
  return isLiningParentAttr(attr);
}

/** 依赖里衬类型的属性：成分 / 克重等 */
export function isLiningDependentAttr(attr) {
  const n = `${attr?.name || ''} ${attr?.nameEn || ''}`;
  if (!/里衬|里料|lining|里布|中里衬/i.test(n)) return false;
  if (isLiningParentAttr(attr)) return false;
  return /克重|成份|成分|composition|weight|gsm|g\/m/i.test(n)
    || /里料(?!纹理|类型)/.test(String(attr?.name || ''))
    || /里衬(?!类型|纹理)/.test(String(attr?.name || ''));
}

/** 父为「无里/无里衬」类时，强制隐藏里衬依赖项（覆盖不完整的 showCondition / parentTemplatePid） */
function isHiddenByNoneLiningParent(attr, productAttributes, allAttrs) {
  if (!isLiningDependentAttr(attr)) return false;
  const parents = (allAttrs || []).filter((a) => isLiningParentAttr(a));
  if (!parents.length) return false;
  return parents.some((parent) => {
    const label = selectedValueLabel(productAttributes, parent);
    return Boolean(label && isNoneLikeAttrValue(label));
  });
}

/**
 * 同名编号组：面料克重1 / 面料克重2 → 同一 key，校验时「至少填一项」。
 */
export function siblingRequiredGroupKey(attr) {
  const name = attributeDisplayLabel(attr) || attr?.name || '';
  const stripped = String(name)
    .replace(/\([^)]*\)/g, '')
    .replace(/\[[^\]]*\]/g, '')
    .trim();
  // 须带末尾数字才成组（避免把「面料克重」与无关项捆在一起）
  if (!/[\d０-９]+\s*$/u.test(stripped)) return '';
  const base = stripped
    .replace(/[\s_\-]*[\d０-９]+\s*$/u, '')
    .replace(/\s+/g, '')
    .toLowerCase();
  if (!base || base.length < 2) return '';
  // 仅对克重/重量类做编号组（用户反馈场景）；其它编号属性保持各自必填
  if (!/克重|重量|gsm|weight|g\/m/i.test(base)) return '';
  return `sib:${base}`;
}

function findParentAttr(allAttrs, parentRef) {
  const ref = Number(parentRef);
  if (!Number.isFinite(ref) || ref <= 0) return null;
  return (allAttrs || []).find((a) => {
    if (Number(a.attributeId) === ref) return true;
    if (Number(a.templatePid) === ref) return true;
    if (attrRefPid(a) === ref) return true;
    const meta = temuMeta(a);
    if (meta && (Number(meta.templatePid) === ref || Number(meta.pid) === ref || Number(meta.refPid) === ref)) {
      return true;
    }
    return false;
  }) || null;
}

/**
 * 当前父属性取值下，该属性是否还有可选枚举值。
 * 全部选项都带 parentVidList 且无一匹配时，整项应对齐平台后台不展示（勿再渲染/提交）。
 */
function hasUsableOptionsForCurrentParents(attr, productAttributes, allAttrs) {
  const values = Array.isArray(attr?.values) ? attr.values : [];
  if (!values.length) return true;
  const constrained = values.filter((v) => Array.isArray(v.parentVidList) && v.parentVidList.length);
  if (!constrained.length) return true;
  const selectedParentVids = collectSelectedEditorParentVids(
    productAttributes,
    allAttrs,
    attr?.attributeId,
  );
  if (!selectedParentVids.size) {
    // 尚无父值：只要还有不受 parentVidList 约束的选项，就应展示（根属性）
    // 仅当「全部选项都绑了父值」时才隐藏，避免把整页类目属性滤成空
    return values.some((v) => !Array.isArray(v.parentVidList) || !v.parentVidList.length);
  }
  return values.some((v) => {
    const parentVidList = Array.isArray(v.parentVidList) ? v.parentVidList.map(String) : [];
    if (!parentVidList.length) return true;
    return parentVidList.some((pv) => selectedParentVids.has(String(pv)));
  });
}

/**
 * 当前属性是否应展示。
 * 对齐平台后台：showCondition / parentTemplatePid / 值级 parentVidList；
 * 当前父取值下无任何可选子值时整项隐藏（例如针织下不出现「面料纹理」）。
 */
export function isEditorAttrVisible(attr, productAttributes, allAttrs) {
  if (!attr) return false;

  // 无里衬：成分/克重等不得展示、不得必填、提交前会被 prune 掉
  if (isHiddenByNoneLiningParent(attr, productAttributes, allAttrs)) {
    return false;
  }

  const conditions = attrShowCondition(attr);
  if (conditions.length > 0) {
    const matched = conditions.some((cond) => {
      const parent = findParentAttr(allAttrs, cond?.parentRefPid);
      if (!parent) return false;
      const selected = resolveEditorAttrSelection(productAttributes, parent);
      if (!selected?.vid) return false;
      const allow = (Array.isArray(cond.parentVids) ? cond.parentVids : []).map(String);
      return allow.includes(String(selected.vid));
    });
    if (!matched) return false;
  } else {
    const parentTid = attrParentTemplatePid(attr);
    if (parentTid != null) {
      const parent = findParentAttr(allAttrs, parentTid);
      // 父不在当前产品属性池时不要整项隐藏（否则类目有属性、页面却全空）
      if (parent && !isEditorAttrFilled(parent, productAttributes)) return false;
    }
  }

  if (!hasUsableOptionsForCurrentParents(attr, productAttributes, allAttrs)) {
    return false;
  }

  return true;
}

/** 原生 attrs 形态兼容（TemuGoodsPublishFlowPage） */
export function isAttrVisible(prop, attrValues, allAttrs) {
  if (!prop) return false;
  const conditions = Array.isArray(prop.showCondition) ? prop.showCondition : [];
  if (conditions.length > 0) {
    return conditions.some((cond) => {
      const parentRef = Number(cond?.parentRefPid);
      const parent = (allAttrs || []).find(
        (a) => Number(a.refPid) === parentRef || Number(a.pid) === parentRef,
      );
      if (!parent) return false;
      const selected = attrValues?.[keyOf(parent)];
      if (!selected || selected.vid == null) return false;
      const allow = (Array.isArray(cond.parentVids) ? cond.parentVids : []).map(String);
      return allow.includes(String(selected.vid));
    });
  }
  if (prop.parentTemplatePid) {
    const parent = (allAttrs || []).find(
      (a) => Number(a.templatePid) === Number(prop.parentTemplatePid),
    );
    if (!parent) return true;
    return Boolean(attrValues?.[keyOf(parent)]);
  }
  return true;
}

export function filterAttrOptionValues(prop, attrValues, allAttrs) {
  const values = Array.isArray(prop?.values) ? prop.values : [];
  if (!values.length) return values;

  const parentLinks = Array.isArray(prop.templatePropertyValueParentList)
    ? prop.templatePropertyValueParentList
    : [];

  const selectedParentVids = new Set();
  for (const a of allAttrs || []) {
    if (Number(a.templatePid) === Number(prop.templatePid)) continue;
    const sel = attrValues?.[keyOf(a)];
    if (sel?.vid != null) selectedParentVids.add(String(sel.vid));
  }

  const filteredByParentList = values.filter((v) => {
    const parentVidList = Array.isArray(v.parentVidList) ? v.parentVidList.map(String) : [];
    if (!parentVidList.length) return true;
    if (!selectedParentVids.size) return true;
    return parentVidList.some((pv) => selectedParentVids.has(String(pv)));
  });

  if (!parentLinks.length) {
    return filteredByParentList.length ? filteredByParentList : values;
  }

  const allowedVids = new Set();
  let anyHit = false;
  for (const link of parentLinks) {
    const need = (Array.isArray(link.parentVidList) ? link.parentVidList : []).map(String);
    const vids = (Array.isArray(link.vidList) ? link.vidList : []).map(String);
    if (!need.length) continue;
    if (need.every((pv) => selectedParentVids.has(pv)) || need.some((pv) => selectedParentVids.has(pv))) {
      anyHit = true;
      vids.forEach((id) => allowedVids.add(id));
    }
  }
  if (!anyHit || !allowedVids.size) {
    return filteredByParentList.length ? filteredByParentList : values;
  }
  const linked = filteredByParentList.filter((v) => allowedVids.has(String(v.vid)));
  return linked.length ? linked : values;
}

/** 收集编辑器表单中已选父属性 vid（按其它属性当前值）。 */
function collectSelectedEditorParentVids(productAttributes, allAttrs, selfAttrId) {
  const selectedParentVids = new Set();
  for (const a of allAttrs || []) {
    if (String(a?.attributeId || '') === String(selfAttrId || '')) continue;
    const sel = resolveEditorAttrSelection(productAttributes, a);
    if (sel?.vid) selectedParentVids.add(String(sel.vid));
  }
  return selectedParentVids;
}

function parentLinksOfEditorAttr(attr) {
  if (Array.isArray(attr?.templatePropertyValueParentList)) {
    return attr.templatePropertyValueParentList;
  }
  const meta = temuMeta(attr);
  return Array.isArray(meta?.templatePropertyValueParentList)
    ? meta.templatePropertyValueParentList
    : [];
}

/**
 * 编辑器形态：按父子依赖过滤可选值（parentVidList / templatePropertyValueParentList）。
 * 无匹配时返回空列表（由 isEditorAttrVisible 隐藏整项），禁止回退成「展示全部」以免提交平台没有的属性值。
 */
export function filterEditorAttrOptionValues(attr, productAttributes, allAttrs) {
  const values = Array.isArray(attr?.values) ? attr.values : [];
  if (!values.length) return values;

  const selectedParentVids = collectSelectedEditorParentVids(
    productAttributes,
    allAttrs,
    attr?.attributeId,
  );
  const parentLinks = parentLinksOfEditorAttr(attr);
  const hasConstrained = values.some((v) => Array.isArray(v.parentVidList) && v.parentVidList.length);

  const filteredByParentList = values.filter((v) => {
    const parentVidList = Array.isArray(v.parentVidList) ? v.parentVidList.map(String) : [];
    if (!parentVidList.length) return true;
    if (!selectedParentVids.size) return false;
    return parentVidList.some((pv) => selectedParentVids.has(String(pv)));
  });

  let result = filteredByParentList;
  if (parentLinks.length) {
    const allowedVids = new Set();
    let anyHit = false;
    for (const link of parentLinks) {
      const need = (Array.isArray(link.parentVidList) ? link.parentVidList : []).map(String);
      const vids = (Array.isArray(link.vidList) ? link.vidList : []).map(String);
      if (!need.length) continue;
      if (need.every((pv) => selectedParentVids.has(pv)) || need.some((pv) => selectedParentVids.has(pv))) {
        anyHit = true;
        vids.forEach((id) => allowedVids.add(id));
      }
    }
    if (anyHit && allowedVids.size > 0) {
      result = filteredByParentList.filter((v) => allowedVids.has(String(v.id)));
    }
  }

  // 有约束但当前父取值下无选项：返回空，交给显隐逻辑隐藏整项
  if (hasConstrained && !result.length) return [];
  return result;
}

/** 当前已选值是否仍满足父子依赖（与下拉过滤同一套规则）。 */
export function isEditorAttrValueAllowedByParent(attr, value, productAttributes, allAttrs) {
  if (value == null || value === '') return true;
  const values = Array.isArray(attr?.values) ? attr.values : [];
  // 手输属性（克重等）无枚举值：不做 parentVidList 校验
  if (!values.length) return true;

  const hasValueParent = values.some((v) => Array.isArray(v.parentVidList) && v.parentVidList.length);
  const hasLinkParent = parentLinksOfEditorAttr(attr).length > 0;
  if (!hasValueParent && !hasLinkParent) return true;

  const allowedIds = new Set(
    filterEditorAttrOptionValues(attr, productAttributes, allAttrs).map((v) => String(v.id)),
  );

  const checkOneVid = (vidRaw) => {
    const vid = String(vidRaw ?? '').trim();
    if (!vid) return true;
    const opt = values.find((v) => String(v.id) === vid);
    // 自定义值（不在模板枚举内）：放行
    if (!opt) return true;
    return allowedIds.has(vid);
  };

  if (Array.isArray(value)) {
    return value.every((row) => {
      if (row && typeof row === 'object') {
        return checkOneVid(row.valueId ?? row.attributeValueId ?? row.vid);
      }
      return checkOneVid(row);
    });
  }
  if (typeof value === 'object') {
    return checkOneVid(value.valueId ?? value.attributeValueId ?? value.vid);
  }
  return checkOneVid(value);
}

/**
 * 清除不满足父子依赖的已填值（父属性变更后调用，避免提交 6000002）。
 */
export function pruneInvalidParentChildProductAttributes(productAttributes, allAttrs) {
  const next = { ...(productAttributes || {}) };
  let changed = false;
  for (const attr of allAttrs || []) {
    const id = keyOf(attr);
    if (!id || next[id] == null || next[id] === '') continue;
    if (isEditorAttrValueAllowedByParent(attr, next[id], next, allAttrs)) continue;
    delete next[id];
    changed = true;
  }
  return changed ? next : (productAttributes || {});
}

/**
 * 先清不可见属性（含：当前织造方式下不应出现的面料纹理等子属性），
 * 再按父子依赖清掉残留非法值；循环直到稳定，避免子属性已隐藏仍被提交。
 */
export function pruneProductAttributesForSubmit(productAttributes, allAttrs) {
  let current = productAttributes || {};
  for (let i = 0; i < 6; i += 1) {
    const afterInvisible = pruneInvisibleProductAttributes(current, allAttrs);
    const afterParent = pruneInvalidParentChildProductAttributes(afterInvisible, allAttrs);
    if (afterParent === current) return current;
    current = afterParent;
  }
  return current;
}

/**
 * 提交前：列出仍填着但不满足父子依赖的属性（应提示用户重选，而不是静默丢掉父值只交子值）。
 * @returns {{ attributeId: string, message: string }[]}
 */
export function collectInvalidParentChildAttrIssues(productAttributes, allAttrs) {
  const issues = [];
  for (const attr of allAttrs || []) {
    const id = keyOf(attr);
    if (!id) continue;
    const value = productAttributes?.[id];
    if (value == null || value === '') continue;
    if (!isEditorAttrVisible(attr, productAttributes, allAttrs)) continue;
    if (isEditorAttrValueAllowedByParent(attr, value, productAttributes, allAttrs)) continue;
    const label = attributeDisplayLabel(attr) || attr?.name || id;
    const sel = resolveEditorAttrSelection(productAttributes, attr);
    const valLabel = String(sel?.propValue || '').trim();
    issues.push({
      attributeId: id,
      message: valLabel
        ? `「${label}」当前值「${valLabel}」与父属性组合不匹配，请清空后按当前父属性重选`
        : `「${label}」与父属性组合不匹配，请清空后重选`,
    });
  }
  return issues;
}

/**
 * 子属性已填、但 parentTemplatePid / showCondition 父属性未填时的提示。
 * @returns {{ attributeId: string, message: string }[]}
 */
export function collectMissingParentAttrIssues(productAttributes, allAttrs) {
  const issues = [];
  for (const attr of allAttrs || []) {
    const id = keyOf(attr);
    if (!id) continue;
    if (!isEditorAttrFilled(attr, productAttributes)) continue;
    if (!isEditorAttrVisible(attr, productAttributes, allAttrs)) continue;

    const conditions = attrShowCondition(attr);
    if (conditions.length > 0) {
      const parentOk = conditions.some((cond) => {
        const parent = findParentAttr(allAttrs, cond?.parentRefPid);
        if (!parent) return false;
        const selected = resolveEditorAttrSelection(productAttributes, parent);
        if (!selected?.vid) return false;
        const allow = (Array.isArray(cond.parentVids) ? cond.parentVids : []).map(String);
        return allow.includes(String(selected.vid));
      });
      if (!parentOk) {
        const label = attributeDisplayLabel(attr) || attr?.name || id;
        issues.push({
          attributeId: id,
          message: `「${label}」缺少合法的父属性值，请先填写对应父属性后再填此项`,
        });
      }
      continue;
    }

    const parentTid = attrParentTemplatePid(attr);
    if (parentTid == null) continue;
    const parent = findParentAttr(allAttrs, parentTid);
    if (!parent) continue;
    if (isEditorAttrFilled(parent, productAttributes)
      && isEditorAttrValueAllowedByParent(
        parent,
        productAttributes?.[keyOf(parent)],
        productAttributes,
        allAttrs,
      )) {
      continue;
    }
    const label = attributeDisplayLabel(attr) || attr?.name || id;
    const parentLabel = attributeDisplayLabel(parent) || parent?.name || String(parentTid);
    issues.push({
      attributeId: id,
      message: `「${label}」依赖「${parentLabel}」，请先填写合法的父属性值`,
    });
  }
  return issues;
}

/**
 * AttributeField.allowedValueIds：有父子约束时返回当前可选 id；无约束返回 null（展示全部）。
 */
export function editorAttrAllowedValueIds(attr, productAttributes, allAttrs) {
  const values = Array.isArray(attr?.values) ? attr.values : [];
  if (!values.length) return null;
  const hasValueParent = values.some((v) => Array.isArray(v.parentVidList) && v.parentVidList.length);
  const hasLinkParent = parentLinksOfEditorAttr(attr).length > 0;
  if (!hasValueParent && !hasLinkParent) return null;
  return filterEditorAttrOptionValues(attr, productAttributes, allAttrs).map((v) => String(v.id));
}

export function missingRequiredVisibleAttrs(productAttrs, attrValues) {
  return (productAttrs || []).filter((p) => {
    if (!p.required) return false;
    if (!isAttrVisible(p, attrValues, productAttrs)) return false;
    const cur = attrValues?.[keyOf(p)];
    return !cur || (cur.vid == null && !String(cur.propValue || '').trim());
  });
}

/**
 * UI：该属性是否仍显示必填星号（可见 + 平台必填 + 编号组未满足时）。
 */
export function isEditorAttrRequiredMark(attr, productAttributes, allAttrs) {
  if (!attr?.required) return false;
  if (!isEditorAttrVisible(attr, productAttributes, allAttrs)) return false;
  const gk = siblingRequiredGroupKey(attr);
  if (!gk) return true;
  const siblings = (allAttrs || []).filter(
    (a) => a?.required
      && siblingRequiredGroupKey(a) === gk
      && isEditorAttrVisible(a, productAttributes, allAttrs),
  );
  if (siblings.length <= 1) return true;
  const anyFilled = siblings.some((a) => isEditorAttrFilled(a, productAttributes));
  if (!anyFilled) return true;
  // 组内已有一项：已填的不再强调；未填的不再标必填
  return false;
}

/**
 * 父属性变更后，清除当前已不可见的子属性值。
 * @returns {Record<string, unknown>} 新的 productAttributes
 */
export function pruneInvisibleProductAttributes(productAttributes, allAttrs) {
  const next = { ...(productAttributes || {}) };
  let changed = false;
  for (const attr of allAttrs || []) {
    const id = keyOf(attr);
    if (!id || next[id] == null || next[id] === '') continue;
    if (isEditorAttrVisible(attr, next, allAttrs)) continue;
    delete next[id];
    changed = true;
  }
  return changed ? next : (productAttributes || {});
}

/**
 * 校验：返回仍须填写的必填属性列表；编号组合并为「至少一项」。
 * @returns {{ singles: object[], groups: { key: string, label: string, members: object[] }[] }}
 */
export function collectEffectivelyRequiredAttrs(allAttrs, productAttributes) {
  const visibleRequired = (allAttrs || []).filter(
    (a) => a?.required && isEditorAttrVisible(a, productAttributes, allAttrs),
  );
  const groups = new Map();
  const singles = [];
  for (const attr of visibleRequired) {
    const gk = siblingRequiredGroupKey(attr);
    if (gk) {
      if (!groups.has(gk)) groups.set(gk, []);
      groups.get(gk).push(attr);
    } else {
      singles.push(attr);
    }
  }
  const groupList = [];
  for (const [key, members] of groups.entries()) {
    if (members.length <= 1) {
      singles.push(...members);
      continue;
    }
    const anyFilled = members.some((a) => isEditorAttrFilled(a, productAttributes));
    if (anyFilled) continue;
    groupList.push({
      key,
      label: members.map((a) => attributeDisplayLabel(a) || a.name || a.attributeId).join(' / '),
      members,
    });
  }
  return { singles, groups: groupList };
}
